import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { AuthenticationService } from '@app/core';
import { BalanceService, CurrencyType } from '@app/core/balance.service';
import { Subscription } from 'rxjs/Subscription';
import { FormBuilder, FormGroup } from '@angular/forms';
import * as QRCode from 'qrcode';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { DepositService } from '@app/core/account/deposit.service';

@Component({
  selector: 'app-deposit-2-modal',
  template: `
    <div class="modal-column">
      <form [formGroup]="form">
        <app-currency-selector formControlName="currency"></app-currency-selector>

        <div class="dw-tabs-menu">
          <button class="dw-tabs-item active">
            <span class="arrow-icon"><i class="fal fa-arrow-up"></i></span> Deposit
          </button>
          <button class="dw-tabs-item" [routerLink]="['/', { outlets: { modal: 'withdraw' } }]">
            <span class="arrow-icon"> <i class="fal fa-arrow-down"></i> </span> Withdraw
          </button>
        </div>

        <ng-container *ngIf="isFiat(currency)">
          <p *ngIf="!walletAddress">Please wait, your deposit wallet is being created.</p>
          <app-fiat-deposit *ngIf="walletAddress" [address]="walletAddress"></app-fiat-deposit>
        </ng-container>

        <ng-container *ngIf="!isFiat(currency)">
          <h5>Your {{ currency }} deposit address</h5>
          <p class="deposit-paragraph">
            This address accepts only {{ currency }}, transferring here anything else will result in fund loss.
          </p>

          <p class="deposit-paragraph" *ngIf="currency === 'ETH'">
            <strong>Notice:</strong> Make sure your gas limit is set to at least <strong>39595</strong> and not to 21000.
            Transaction will be canceled if gas limit is less than <strong>39595</strong>.
          </p>

          <div class="deposit-address-wrap">
            <div class="address" [cbContent]="walletAddress" ngxClipboard>
              {{ walletAddress ? walletAddress : 'Creating new ' + currency + ' wallet' }}
            </div>
            <div class="deposit-right-flex">
              <button class="qr-code-small" [disabled]="!walletAddress" (click)="showQr = !showQr"></button>
              <button class="copy-address" [disabled]="!walletAddress" ngxClipboard [cbContent]="walletAddress">
                <i class="fal fa-copy"></i>
              </button>
            </div>
          </div>

          <div class="deposit-limits">
            Minimum deposit amount is <span class="deposit-limit-feature">{{ minAmount | crypto: currency }}</span
            >. Maximum deposit is <span class="deposit-limit-feature">{{ maxAmount | crypto: currency }}</span
            >.
          </div>
          <div class="deposit-limits">
            If your deposit amount will be lower than
            <span class="deposit-limit-feature">{{ minAmount | crypto: currency }}</span
            >, <span class="deposit-limit-feature">{{ feeAmount | crypto: currency }}</span> will be deducted from your
            balance.
          </div>

          <div class="deposit-limits" *ngIf="currency === 'ETH'">
            <strong>Warning:</strong> Do not deposit from smart contract address, deposit from your wallet, otherwise
            you will lose your coins/money.
          </div>

          <app-deposit-bonus-notice></app-deposit-bonus-notice>

          <div class="qr-code-big-wrap" *ngIf="showQr">
            <div class="qr-code-big">
              <ng-container *ngIf="appUrl">
                <a [href]="appUrl" rel="nofollow noopener noreferral">
                  <img [src]="qrLargeUrl" [alt]="walletAddress" />
                </a>
              </ng-container>
              <ng-container *ngIf="!appUrl"> <img [src]="qrLargeUrl" [alt]="walletAddress" /> </ng-container>
            </div>
          </div>
        </ng-container>
      </form>
    </div>

    <a
      [routerLink]="['/', { outlets: { modal: null } }]"
      class="icon close-popup"
      (click)="trackDepositClose()"
      data-id="deposit-close-href"
    >
      <i class="fal fa-times"></i>
    </a>
  `,
  styleUrls: ['./deposit-modal-2.template.scss', './tabs.scss'],
})
export class DepositModal2Component implements OnInit, OnDestroy, AfterViewInit {
  public form: FormGroup;
  public walletAddress: string;
  public currency: string = this.balance
    .getCurrency()
    .toString()
    .toUpperCase();
  public showQr: boolean;
  public minAmount: number = 0;
  public maxAmount: number = 0;
  public feeAmount: number = 0;

  public qrLargeUrl: SafeResourceUrl;
  public appUrl: SafeUrl;

  private subs: Subscription = new Subscription();
  private loadAddressData: Subscription;

  constructor(
    private auth: AuthenticationService,
    private balance: BalanceService,
    private depositService: DepositService,
    private formBuilder: FormBuilder,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit() {
    this.createForm();
    this.getWalletData();
    this.subs.add(
      this.form.get('currency').valueChanges.subscribe(value => {
        this.currency = value.toString().toUpperCase();
        this.getWalletData();
      })
    );
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (this.loadAddressData) {
      this.loadAddressData.unsubscribe();
    }
  }

  getWalletData(): void {
    if (this.loadAddressData) {
      this.loadAddressData.unsubscribe();
    }
    this.walletAddress = null;
    this.loadAddressData = this.depositService.getWallet(this.currency.toLowerCase()).subscribe(wallet => {
      this.walletAddress = wallet.address || null;
      this.minAmount = wallet.minAmount;
      this.maxAmount = wallet.maxAmount;
      this.feeAmount = wallet.fee;
      this.generateQrImages();
    });
  }

  trackDepositClose() {
    // if (this.isOnboarding) {
    //   amplitude.getInstance().logEvent('Onboarding - Close');
    // } else {
    //   amplitude.getInstance().logEvent('Deposit - Close');
    // }
  }

  private toQrCodeDataUrl(
    value: string,
    { margin, scale, width, colorDark, colorLight, version, errorCorrectionLevel }: Partial<QrCodeOptions>
  ): Promise<SafeResourceUrl | never> {
    return new Promise((resolve, reject) => {
      QRCode.toDataURL(
        value,
        <any>{
          version: version || '',
          errorCorrectionLevel: errorCorrectionLevel || 'M',
          margin: margin || 4,
          scale: scale || 4,
          width: width || 10,
          color: {
            dark: colorDark || '#000000',
            light: colorLight || '#ffffff',
          },
        },
        (err, url: string) => {
          if (err) {
            reject(err);
          } else {
            resolve(this.sanitizer.bypassSecurityTrustResourceUrl(url));
          }
        }
      );
    });
  }

  private async generateQrImages() {
    let url: string = null;
    if (this.walletAddress) {
      const schema = this.appSchemas(this.currency);
      if (schema) {
        url = `${schema}:${this.walletAddress}`;
      }
    }
    this.qrLargeUrl = await this.toQrCodeDataUrl(url || this.walletAddress, { margin: 2 });
    this.appUrl = url ? this.sanitizer.bypassSecurityTrustUrl(url) : null;
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      currency: this.balance.getCurrency(),
    });
  }

  private appSchemas(currency: string): string {
    const schemas = {
      btc: 'bitcoin',
      eth: 'ethereum',
      ltc: 'litecoin',
      edg: 'ethereum',
      dai: 'ethereum',
    };
    if (schemas.hasOwnProperty(currency.toLowerCase())) {
      return schemas[currency.toLowerCase()];
    }
    return null;
  }

  public isFiat(currency: string) {
    return [CurrencyType.EUR, CurrencyType.USD, CurrencyType.USDT20, CurrencyType.DAI].includes(
      currency.toLowerCase() as CurrencyType
    );
  }
}

interface QrCodeOptions {
  margin: number | string;
  scale: null | string;
  width: number;
  colorDark: string;
  colorLight: string;
  version: string | number;
  errorCorrectionLevel: string;
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/Prod_web-client/src/app/_v2/template/modals/deposit/deposit-modal-2.component.ts